import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface TestRun {
  id: string;
  commit_sha: string;
  commit_message: string | null;
  branch_name: string;
  author_name: string | null;
  author_email: string | null;
  workflow_run_id: number | null;
  workflow_name: string | null;
  status: 'running' | 'passed' | 'failed' | 'error';
  total_tests: number;
  passed: number;
  failed: number;
  skipped: number;
  duration: number | null;
  coverage_lines: number | null;
  coverage_statements: number | null;
  coverage_functions: number | null;
  coverage_branches: number | null;
  datadog_trace_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface TestResult {
  id: string;
  test_run_id: string;
  test_name: string;
  test_file: string;
  status: 'passed' | 'failed' | 'skipped';
  duration: number | null;
  error_message: string | null;
  error_stack: string | null;
  created_at: string;
}

export const useTestRuns = () => {
  const [runs, setRuns] = useState<TestRun[]>([]);
  const [selectedRun, setSelectedRun] = useState<TestRun | null>(null);
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch all test runs
  const fetchTestRuns = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const { data, error: fetchError } = await supabase
        .from('test_runs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);

      if (fetchError) throw fetchError;

      if (data) {
        setRuns(data as TestRun[]);
        // Auto-select first run if none selected
        if (data.length > 0 && !selectedRun) {
          setSelectedRun(data[0] as TestRun);
        }
      }
    } catch (err) {
      console.error('Error fetching test runs:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch test runs');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch test results for selected run
  const fetchTestResults = async (runId: string) => {
    try {
      const { data, error: fetchError } = await supabase
        .from('test_results')
        .select('*')
        .eq('test_run_id', runId)
        .order('test_file', { ascending: true });

      if (fetchError) throw fetchError;

      if (data) {
        setTestResults(data as TestResult[]);
      }
    } catch (err) {
      console.error('Error fetching test results:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch test results');
    }
  };

  useEffect(() => {
    fetchTestRuns();
  }, []);

  useEffect(() => {
    if (selectedRun) {
      fetchTestResults(selectedRun.id);
    }
  }, [selectedRun]);

  return {
    runs,
    selectedRun,
    setSelectedRun,
    testResults,
    isLoading,
    error,
    refetch: fetchTestRuns
  };
};
